import { useState, Suspense, Fragment, useMemo, useEffect } from 'react';

import {
  Box,
  DialogProps,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import axios from 'axios';
import useAxios from 'axios-hooks';

import { Loader, AsyncAutocomplete } from 'components';
import { AppDialog } from 'components/Dialogs';
import { formatProperty } from 'components/ChangeDataTable/ChangeDataTable';
import { useTheme } from 'contexts/ThemeContext';
import strings from 'constants/index';
import type { ChangesLogData } from 'features/changes';
import { getReactJsonTheme, LazyReactJson as ReactJson } from 'utils/react-json.utils';

import { LogsTable } from './components/LogTable';
import { useLogTableStyles } from './components/LogTable.styles';

interface LogsDialogProps extends DialogProps {
  entityName: string;
  logsUrl: string;
  additionalSpecialProperties?: string[];
  entityNameMappings?: Record<string, string>;
}

interface LogsData {
  data: ChangesLogData[];
  count: number;
}

const DEFAULT_SPECIAL_PROPERTIES = ['ModifiedOn', 'CreatedOn', 'UpdatesCount', 'DeletedOn', 'Id'];

const getStorageKey = (url: string) => {
  const path = url.split('?')[0].replace(/\/\d+/g, '/:id');
  return `logsDialog_hiddenProperties_${path}`;
};

// If at any point there is too much common props
// between dialogs, can be solved with a HOC
const LogsDialog = ({
  open,
  onClose,
  entityName,
  logsUrl,
  additionalSpecialProperties = [],
  entityNameMappings = {},
}: LogsDialogProps) => {
  const { themeMode } = useTheme();
  const [extendedView, setExtendedView] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [propertyFilterKey] = useState(0);
  const [hiddenProperties, setHiddenProperties] = useState<Array<{ id: number; name: string }>>([]);
  const [entityNames, setEntityNames] = useState<Record<string, string>>({});
  const SPECIAL_PROPERTIES = useMemo(
    () => [...DEFAULT_SPECIAL_PROPERTIES, ...additionalSpecialProperties],
    [additionalSpecialProperties],
  );
  const styles = useLogTableStyles();
  const [{ data }] = useAxios<LogsData>(logsUrl, {
    manual: !open,
  });

  useEffect(() => {
    if (open && logsUrl) {
      const storageKey = getStorageKey(logsUrl);
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setHiddenProperties(parsed);
        } catch (e) {
          console.error('Failed to parse stored hidden properties', e);
        }
      }
    }
  }, [open, logsUrl]);

  useEffect(() => {
    if (!data?.data || Object.keys(entityNameMappings).length === 0) {
      return;
    }

    const fetchEntityNames = async () => {
      const idsToFetch: Record<string, Set<string>> = {};

      data.data.forEach(log => {
        log.changes.forEach(change => {
          const { propertyName, oldValue, currentValue } = change;

          Object.keys(entityNameMappings).forEach(mappingKey => {
            if (propertyName.includes(mappingKey)) {
              if (!idsToFetch[mappingKey]) {
                idsToFetch[mappingKey] = new Set();
              }

              const extractIds = (value: any) => {
                if (value === null || value === undefined) return;
                const valueStr = String(value);
                const numericMatch = valueStr.match(/\b\d+\b/g);

                if (numericMatch) {
                  numericMatch.forEach(id => idsToFetch[mappingKey].add(id));
                }
              };

              extractIds(oldValue);
              extractIds(currentValue);
            }
          });
        });
      });

      const newEntityNames: Record<string, string> = {};

      for (const [mappingKey, ids] of Object.entries(idsToFetch)) {
        if (ids.size === 0) continue;

        const url = entityNameMappings[mappingKey];
        const idsArray = Array.from(ids);
        const filter = `filter=id[in]:${idsArray.join(',')}`;

        try {
          const response = await axios.get(`${url}?pageSize=200&${filter}`);
          const entities = response.data?.items || response.data || [];

          entities.forEach((entity: any) => {
            const id = entity.id;
            const name = entity.name;

            if (id && name) {
              newEntityNames[id] = name;
            }
          });
        } catch (error) {
          console.error(`Failed to fetch entity names from ${url}:`, error);
        }
      }

      setEntityNames(newEntityNames);
    };

    fetchEntityNames();
  }, [data, entityNameMappings]);
  const maxWidth = data?.count > 0 ? 'xl' : 'sm';

  const dataForTable = useMemo(() => {
    if (!data?.data) return undefined;

    const enhanceValue = (value: any, propertyName: string): any => {
      if (value === null || value === undefined || Object.keys(entityNameMappings).length === 0) {
        return value;
      }

      let valueStr = String(value);

      for (const mappingKey of Object.keys(entityNameMappings)) {
        if (propertyName.includes(mappingKey)) {
          for (const [id, name] of Object.entries(entityNames)) {
            const regex = new RegExp(`\\b${id}\\b`, 'g');
            valueStr = valueStr.replace(regex, `${id} (${name})`);
          }
          break;
        }
      }

      return valueStr;
    };

    return data.data.flatMap(({ changes, ...rest }, index) =>
      changes.map(change => ({
        ...change,
        oldValue: enhanceValue(change.oldValue, change.propertyName),
        currentValue: enhanceValue(change.currentValue, change.propertyName),
        ...rest,
        isOdd: index % 2 === 0,
      })),
    );
  }, [data, entityNames, entityNameMappings]);

  const propertyOptions = useMemo(() => {
    if (!dataForTable) return [];
    const uniqueProperties = new Set<string>();
    const specialPropertiesFound = new Set<string>();

    dataForTable.forEach(item => {
      const formatted = formatProperty(item);
      if (formatted.propertyName) {
        uniqueProperties.add(formatted.propertyName);

        // Check if this property contains any special properties
        for (const specialProp of SPECIAL_PROPERTIES) {
          if (
            formatted.propertyName.endsWith(`.${specialProp}`) ||
            formatted.propertyName === specialProp
          ) {
            specialPropertiesFound.add(specialProp);
          }
        }
      }
    });

    specialPropertiesFound.forEach(specialProp => {
      if (!uniqueProperties.has(specialProp)) {
        uniqueProperties.add(specialProp);
      }
    });

    return Array.from(uniqueProperties)
      .sort()
      .map((name, index) => ({ id: index, name }));
  }, [dataForTable]);

  const filteredData = useMemo(() => {
    if (!dataForTable) return dataForTable;

    let result = dataForTable;

    if (selectedProperty) {
      result = result.filter(item => {
        const formatted = formatProperty(item);
        return formatted.propertyName === selectedProperty;
      });
    }

    if (hiddenProperties.length > 0) {
      const hiddenNames = new Set(hiddenProperties.map(p => p.name));
      result = result.filter(item => {
        const formatted = formatProperty(item);
        const propertyName = formatted.propertyName;

        if (!propertyName) return true;

        if (hiddenNames.has(propertyName)) return false;

        for (const specialProp of SPECIAL_PROPERTIES) {
          if (hiddenNames.has(specialProp)) {
            if (propertyName.endsWith(`.${specialProp}`) || propertyName === specialProp) {
              return false;
            }
          }
        }

        return true;
      });
    }

    return result;
  }, [dataForTable, selectedProperty, hiddenProperties]);
  return (
    <AppDialog
      open={open}
      title={`${strings.logs_for} ${entityName}`}
      maxWidth={maxWidth}
      onClose={onClose}
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '80vh',
        },
      }}
    >
      <Box mb={1} display="flex" flexDirection="column" height="100%">
        {data?.count > 0 ? (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box flex={1} mr={1}>
                <AsyncAutocomplete
                  key={propertyFilterKey}
                  label={strings.property_name}
                  dataSource={propertyOptions}
                  defaultValue={
                    selectedProperty
                      ? propertyOptions.find(opt => opt.name === selectedProperty) || null
                      : null
                  }
                  onChange={(value: { id: number; name: string } | null) => {
                    setSelectedProperty(value?.name || null);
                  }}
                  textFieldProps={{
                    placeholder: 'All properties',
                  }}
                />
              </Box>

              <Box flex={1} mr={1}>
                <AsyncAutocomplete
                  multiple
                  label="Hide Properties"
                  dataSource={propertyOptions}
                  defaultValue={hiddenProperties}
                  onChange={(value: Array<{ id: number; name: string }> | null) => {
                    const newHiddenProperties = value || [];
                    setHiddenProperties(newHiddenProperties);

                    const storageKey = getStorageKey(logsUrl);
                    localStorage.setItem(storageKey, JSON.stringify(newHiddenProperties));
                  }}
                  textFieldProps={{
                    placeholder: 'Hide properties from logs',
                  }}
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    data-testid="extended-view-switch"
                    aria-label="extended-view"
                    checked={extendedView}
                    onChange={() => setExtendedView(!extendedView)}
                    name="extendedView"
                  />
                }
                label={strings.extended_view}
              />
            </Box>
            {extendedView ? (
              <Suspense fallback={<Loader />}>
                {data?.data?.map(log => (
                  <Fragment key={log.elasticId}>
                    <ReactJson
                      src={log}
                      theme={getReactJsonTheme(themeMode)}
                      displayDataTypes={false}
                      displayObjectSize={false}
                    />
                    <Divider className={styles.divider} />
                  </Fragment>
                ))}
              </Suspense>
            ) : (
              <LogsTable data={filteredData}></LogsTable>
            )}
          </>
        ) : (
          <Typography variant="h6" align="center">
            {strings.no_logs_available}
          </Typography>
        )}
      </Box>
    </AppDialog>
  );
};

export default LogsDialog;
